import axios from "axios";
import React, { useEffect, useState } from "react";
import { api } from "../utils/Api";
import { Typography, Flex, Button, notification, Tag, Statistic } from "antd";
import Layout from "../layout/Layout";
import {
	CustomSearch,
	CustomSpinner,
	CustomTable,
} from "../utils/CustomComponents";
import dayjs from "dayjs";
import { RetweetOutlined, RollbackOutlined } from "@ant-design/icons";
import QAQCJobLoadsInternalModal from "./QAQCJobLoadsInternalModal";
const { Text } = Typography;

const QAQCLaboratoryTestInternalReport = ({ history }) => {
	const [loading, setLoading] = useState(true);
	const [loadingTable, setLoadingTable] = useState(false);
	const [jobList, setJobList] = useState([]);
	const [startDate, setStartDate] = useState(dayjs());
	const [endDate, setEndDate] = useState(dayjs());
	const [apiNotification, contextHolder] = notification.useNotification();

	const [refresh, setRefresh] = useState(false);

	const [modalOpenLoads, setModalOpenLoads] = useState(false);
	const [hasLaboratoryLoads, setHasLaboratoryLoads] = useState(true);
	const [selectedJob, setSelectedJob] = useState(null);

	const openNotification = (type, message, description) => {
		apiNotification[type]({
			message: message,
			description: description,
		});
	};

	/**
	 ** Options header (columns)
	 */
	const columns = [
		{
			title: "Date",
			dataIndex: "scheduledDate",
			key: "scheduledDate",
			render: (value) => {
				return <Text>{value}</Text>;
			},
		},
		{
			title: "Job Number",
			dataIndex: "number",
			key: "number",
			render: (value, record) => {
				return (
					<Text
						strong
						underline
						style={{ cursor: "pointer" }}
						onClick={() => {
							setSelectedJob(record);
							setModalOpenLoads(true);
						}}
					>
						{value}
					</Text>
				);
			},
		},
		{
			title: "Customer",
			dataIndex: "customer",
			key: "customer",
			render: (value) => {
				return <Text>{value}</Text>;
			},
		},
		{
			title: "Plant",
			dataIndex: "plant",
			key: "plant",
			render: (value) => {
				return <Text>{value}</Text>;
			},
		},
		{
			title: "Address",
			dataIndex: "address",
			key: "address",
			render: (value) => {
				return <Text>{value}</Text>;
			},
		},
		{
			title: "Seller",
			dataIndex: "seller",
			key: "seller",
			render: (value) => {
				return <Text>{value}</Text>;
			},
		},
		{
			title: "Yards",
			dataIndex: "yards",
			key: "yards",
			render: (value) => {
				return (
					<Statistic
						value={value}
						valueStyle={{
							fontSize: 14,
						}}
					/>
				);
			},
		},
		{
			title: "QC Type",
			dataIndex: "qcType",
			key: "qcType",
			render: (value) => {
				return (
					<Tag color={value === "Internal" ? "purple" : "yellow"}>{value}</Tag>
				);
			},
		},
		{
			title: "Franchise",
			dataIndex: "franchise",
			key: "franchise",
			render: (value) => {
				return (
					<Tag
						color={
							value === "57Concrete"
								? "blue"
								: value === "Castle"
									? "green"
									: "cyan"
						}
					>
						{value}
					</Tag>
				);
			},
		},
	];

	useEffect(() => {
		getJobList();
	}, [startDate, endDate, hasLaboratoryLoads, refresh]);

	const getJobList = async () => {
		try {
			setLoading(true);
			setLoadingTable(true);

			const start = `${startDate.year()}-${
				startDate.month() + 1
			}-${startDate.date()}`;
			const end = `${endDate.year()}-${endDate.month() + 1}-${endDate.date()}`;

			const url =
				api +
				`/job/get/qc/laboratory/Internal?startDate=${start}&endDate=${end}&hasLaboratoryLoads=${hasLaboratoryLoads}`;

			const token = localStorage.getItem("token");
			axios.defaults.headers.common = { Authorization: `Bearer ${token}` };

			const { data } = await axios.get(url);
			if (data.success) {
				let list = [];
				data.payload.map((item, index) => {
					list.push({
						key: index,
						id: item._id,
						number: item.number,
						qcType: item.qcType ?? "-",
						franchise: item.franchise || "57Concrete",
						customer: item.customer.name,
						seller: item.seller.name,
						scheduledDate: dayjs(item.scheduledDate).format(
							"MM/DD/YYYY hh:mm A",
						),
						address: `${item.customerShipAddress} - (${item.shipAddress})`,
						yards: item.loadYD3,
						plant: item.plant.name,
					});
				});
				setJobList(list);
				setLoadingTable(false);
			} else {
				openNotification("error", "Error", data.message);
			}
			setLoading(false);
		} catch (err) {
			console.error(err.message);
			openNotification("error", "Error", err.message);
			return null;
		}
	};

	const RenderTable = () => {
		return (
			<CustomTable columns={columns} data={jobList} loading={loadingTable} />
		);
	};

	const onRangeChange = (dates) => {
		if (dates) {
			setStartDate(dates[0]);
			setEndDate(dates[1]);
		}
	};

	const RenderSearch = () => (
		<Flex gap={10} align="end" justify="space-between">
			<CustomSearch
				loading={loading}
				startDate={startDate}
				endDate={endDate}
				onRangeChange={onRangeChange}
				dateFormat={"MM/DD/YYYY"}
				pickerValue={hasLaboratoryLoads}
				pickerWidth={300}
				labelPicker={"Filter By Testing Loads"}
				onChangePicker={(value) => setHasLaboratoryLoads(value)}
				allowClearPicker={false}
				optionsPicker={[
					{
						value: true,
						label: "With Laboratory Testing Loads",
					},
					{
						value: false,
						label: "Without Laboratory Testing Loads",
					},
				]}
			/>
			<Button
				type="primary"
				onClick={() => setRefresh((prev) => !prev)}
				icon={<RetweetOutlined />}
				size="large"
				loading={loading}
			/>
		</Flex>
	);

	const goBack = () => {
		history.goBack();
	};

	return (
		<Layout
			title="QA/QC Internal Sampling Dashboard"
			description="Internal Dashboard"
		>
			<Flex vertical gap={16}>
				<Button
					type="primary"
					size="large"
					icon={<RollbackOutlined />}
					onClick={goBack}
					loading={loading}
					disabled={loading}
				/>
				<RenderSearch />
				{loading ? <CustomSpinner /> : <RenderTable />}
				{contextHolder}
				<QAQCJobLoadsInternalModal
					modalOpenLoads={modalOpenLoads}
					setModalOpenLoads={setModalOpenLoads}
					selectedJob={selectedJob}
					setSelectedJob={setSelectedJob}
					history={history}
				/>
			</Flex>
		</Layout>
	);
};

export default QAQCLaboratoryTestInternalReport;
